import { zodResolver } from "@hookform/resolvers/zod"
import { memo, useMemo, useRef, useState } from "react"
import type { Resolver } from "react-hook-form"
import { FaPlus } from "react-icons/fa"

import { useCreateClassSession } from "@/hooks/api/useClassSessions"
import { usePermissions } from "@/hooks/usePermissions"
import { class_sessionUpdateSchema } from "@/types/class_session"
import type {
  AddClassSessionProps,
  ClassSessionCreateInput,
  ClassSessionUpdateInput,
} from "@/types/class_session"
import { Button } from "@chakra-ui/react"

import ClassSessionFormDialog from "./ClassSessionFormDialog"

const AddClassSession = memo(
  ({ onSuccess, onCancel }: AddClassSessionProps = {}) => {
    const [isOpen, setIsOpen] = useState(false)
    const { hasPermission } = usePermissions()
    const createClassSessionMutation = useCreateClassSession()
    const addButtonRef = useRef<HTMLButtonElement | null>(null)

    if (!hasPermission("class_sessions:create")) {
      return null
    }

    const defaultValues = useMemo<ClassSessionUpdateInput>(
      () => ({
        class_template_id: "",
        session_id: null,
        instructor_id: "",
        session_date: "",
        session_start: "",
        session_end: "",
        status: "scheduled",
        cancellation_reason: "",
        location_override: "",
        max_students_override: null,
        notes: "",
      }),
      [],
    )

    const handleClose = () => {
      setIsOpen(false)
      onCancel?.()
    }

    const handleSubmit = async (data: ClassSessionUpdateInput) => {
      // Cast to CreateInput since all required fields will be present after form validation
      await createClassSessionMutation.mutateAsync(data as ClassSessionCreateInput)
      onSuccess?.()
    }

    return (
      <>
        <Button
          ref={addButtonRef}
          onClick={() => setIsOpen(true)}
          colorPalette="blue"
          size="sm"
          aria-label="Add new class meeting"
        >
          <FaPlus fontSize="14px" aria-hidden="true" />
          Add Class Meeting
        </Button>

        <ClassSessionFormDialog
          isOpen={isOpen}
          title="Add New Class Meeting"
          submitLabel="Create Class Meeting"
          /* TODO: zodResolver generic mismatch - CreateSchema infers CreateInput but dialog expects Resolver<UpdateInput> */
          resolver={zodResolver(class_sessionUpdateSchema) as Resolver<ClassSessionUpdateInput>}
          defaultValues={defaultValues}
          onSubmit={handleSubmit}
          onClose={handleClose}
          isSubmitting={createClassSessionMutation.isPending}
          finalFocusRef={addButtonRef}
          showSkipLink
          loadingText="Creating..."
        />
      </>
    )
  },
)

AddClassSession.displayName = "AddClassSession"

export default AddClassSession
export type { AddClassSessionProps }
